import { formatShare } from "#app/widgets/definitions/shared/format-share";
import { Box, Skeleton, Text, WidgetDisclosureRowSkeleton } from "@salestrak/ui";
import { type OutcomePerformanceUserRow } from "./use-outcome-performance";
import { type MemberColumn } from "./vocabulary";

/** The figures a member row and an entity row share. */
type OutcomeFigures = Pick<OutcomePerformanceUserRow, "total" | "positive" | "completed" | "completionRate">;

/** One figure cell: the number the header's column names, tabular so the
 *  column reads as one. Positive carries its denominator quietly. */
export function OutcomeFigure({ row, column }: { row: OutcomeFigures; column: MemberColumn["key"] }) {
  switch (column) {
    case "updates":
      return (
        <Text size="sm" weight="semibold" tabular>
          {row.total.toLocaleString()}
        </Text>
      );
    case "positive":
      return (
        <Box direction="row" align="baseline" gap="xs">
          <Text size="sm" weight="semibold" tabular>
            {row.positive.toLocaleString()}
          </Text>
          <Text size="xs" variant="muted" tabular>
            of {row.completed.toLocaleString()}
          </Text>
        </Box>
      );
    case "completed":
      return (
        <Text size="sm" weight="semibold" tabular>
          {formatShare(row.completionRate, 1)}
        </Text>
      );
  }
}

/** A member row's placeholder: the chip and one figure per column, on the
 *  same widths, so the list holds its geometry while the figures land. */
export function MemberRowSkeleton({ columns, selected }: { columns: readonly MemberColumn[]; selected: boolean }) {
  return (
    <WidgetDisclosureRowSkeleton selected={selected}>
      <Box fill="flex" direction="row" align="center" gap="xs" shrink minWidth={0}>
        <Skeleton delayed circle width={24} />
        <Skeleton delayed height={10} width={120} />
      </Box>
      {columns.map((column) => (
        <Box key={column.key} width={column.width} shrink={false} align="end">
          <Skeleton delayed height={10} width={28} />
        </Box>
      ))}
    </WidgetDisclosureRowSkeleton>
  );
}
